/**
 * RoadmapCommentRow
 *
 * Renders a single root-level roadmap ("Ideas") comment along with its
 * (optionally expanded) replies. Structural sibling of the content CommentRow,
 * swapping CommentEntity for RoadmapCommentEntity.
 *
 * Roadmap divergences (verified against Flutter roadmap_comment_widget.dart):
 *   - The like button is rendered ONLY here (root comments). Replies render via
 *     RoadmapCommentReplyRow, which has no like control (Flutter `:446`).
 *   - No media thumbnail — roadmap comments carry no mediaInfo.
 *   - "You"/" > {name}" naming matches `:212-225`.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { Navigator } from '@/presentation/routing/Navigator';
import type { RootState } from '@/presentation/store';
import { RoadmapCommentReplyRow } from './RoadmapCommentReplyRow';
import { RoadmapCommentLikeButton } from './RoadmapCommentLikeButton';
import { renderMentionText } from '@/presentation/feature/content/components/sheets/comments/components/utils/renderMentionText';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { RoadmapCommentEntity } from '@/domain/entities/roadmapComment/roadmapCommentEntity';

export interface RoadmapCommentRowProps {
  comment: RoadmapCommentEntity;
  highlightedCommentId: string | null;
  onReply: (comment: RoadmapCommentEntity) => void;
  onExpandReplies: (comment: RoadmapCommentEntity) => void;
  onCollapseReplies: (comment: RoadmapCommentEntity) => void;
  onLike: (comment: RoadmapCommentEntity, liked: boolean) => void;
}

export const RoadmapCommentRow: React.FC<RoadmapCommentRowProps> = ({
  comment,
  highlightedCommentId,
  onReply,
  onExpandReplies,
  onCollapseReplies,
  onLike,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const isHighlighted = highlightedCommentId === comment.id;
  const hasExpandedChildren = (comment.children?.length ?? 0) > 0;
  const canExpandMore = comment.replies > (comment.children?.length ?? 0);
  const displayName = currentUser?.firebaseId === comment.userId ? l10n.comments.you : comment.userName;

  return (
    <div
      {...qaAttr({ id: `roadmap-comment-${comment.id}` })}
      style={{
        padding: responsive.spacing.lg,
        backgroundColor: isHighlighted ? theme.secondary : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: responsive.gap.sm,
      }}
    >
      <div style={{ display: 'flex', gap: responsive.gap.base }}>
        <div
          onClick={() => Navigator.navigateToUserDetails(comment.userId)}
          style={{ cursor: 'pointer', flexShrink: 0 }}
        >
          <CachedImage
            src={UserUtils.generateUserProfileImageUrl(comment.userId)}
            alt={comment.userName}
            style={{
              width: responsive.avatar.medium,
              height: responsive.avatar.medium,
              borderRadius: responsive.borderRadius.round,
              objectFit: 'cover',
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: responsive.gap.xs }}>
            <span
              onClick={() => Navigator.navigateToUserDetails(comment.userId)}
              style={{
                fontWeight: responsive.fontWeight.semibold,
                color: theme.text.primary,
                fontSize: responsive.font.sm,
                cursor: 'pointer',
              }}
            >
              {displayName}
            </span>
          </div>
          <div
            style={{
              color: theme.text.primary,
              fontSize: responsive.font.base,
              marginTop: responsive.spacing.xxs,
              wordBreak: 'break-word',
            }}
          >
            {renderMentionText(comment.text, theme.exclusive)}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: responsive.gap.base,
              marginTop: responsive.spacing.sm,
            }}
          >
            <span
              style={{
                fontSize: responsive.font.xs,
                color: theme.text.secondary,
              }}
            >
              {dateFormatters.formatDate(comment.createdTimestamp)}
            </span>
            <button
              {...qaAttr({ id: `roadmap-comment-reply-action-${comment.id}` })}
              onClick={() => onReply(comment)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme.text.primary,
                fontSize: responsive.font.xs,
                fontWeight: responsive.fontWeight.semibold,
                padding: 0,
              }}
            >
              {l10n.comments.reply}
            </button>
          </div>
        </div>
        <RoadmapCommentLikeButton
          commentId={comment.id}
          likes={comment.likes}
          size={responsive.icon.small}
          onToggle={liked => onLike(comment, liked)}
        />
      </div>

      {hasExpandedChildren && (
        <div
          style={{
            marginLeft: responsive.avatar.medium + responsive.spacing.base,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {comment.children!.map(child => (
            <RoadmapCommentReplyRow
              key={child.id}
              reply={child}
              highlighted={highlightedCommentId === child.id}
              onReply={onReply}
            />
          ))}
        </div>
      )}

      {(canExpandMore || hasExpandedChildren) && (
        <div
          style={{
            marginLeft: responsive.avatar.medium + responsive.spacing.base,
            display: 'flex',
            alignItems: 'center',
            gap: responsive.gap.base,
          }}
        >
          {canExpandMore && (
            <button
              {...qaAttr({ id: `roadmap-comment-view-replies-${comment.id}` })}
              onClick={() => onExpandReplies(comment)}
              disabled={comment.isFetchingChildren}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme.text.secondary,
                fontSize: responsive.font.xs,
                padding: 0,
              }}
            >
              {hasExpandedChildren
                ? l10n.comments.viewMoreReplies.replace(
                    '{count}',
                    String(comment.replies - (comment.children?.length ?? 0)),
                  )
                : l10n.comments.viewReplies.replace('{count}', String(comment.replies))}
            </button>
          )}
          {hasExpandedChildren && (
            <button
              {...qaAttr({ id: `roadmap-comment-hide-replies-${comment.id}` })}
              onClick={() => onCollapseReplies(comment)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: theme.text.secondary,
                fontSize: responsive.font.xs,
                padding: 0,
              }}
            >
              {l10n.comments.hide}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
